import React from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

import { IManualIncome } from '@/types'
import { formatDay, formatDollars, formatEnum } from '../format'
import { ManualPayCycleDecision } from './status'

interface ReviewManualPayCycleDialogProps {
  decision: ManualPayCycleDecision | null
  // The approval went through; the dialog now offers income detection as the next step.
  approved: boolean
  manualIncome: IManualIncome
  isSaving: boolean
  // Set after a review failed; the dialog stays open so it can be retried or backed out of.
  error?: string
  onCancel: () => void
  onConfirm: () => void
  onDetectIncome: () => void
  detectionBusy: boolean
}

const COPY: Record<
  ManualPayCycleDecision,
  { title: string; action: string; consequence: string; destructive: boolean }
> = {
  approve: {
    title: 'Approve manual pay cycle',
    action: 'Approve',
    consequence: 'The customer will be assessed using this pay cycle.',
    destructive: false,
  },
  reject: {
    title: 'Reject manual pay cycle',
    action: 'Reject',
    consequence: 'The customer will need to submit their pay cycle again.',
    destructive: true,
  },
}

// A review changes how the customer is assessed, so what is being approved or rejected is shown
// back before it is sent.
export const ReviewManualPayCycleDialog: React.FC<ReviewManualPayCycleDialogProps> = ({
  decision,
  approved,
  manualIncome,
  isSaving,
  error,
  onCancel,
  onConfirm,
  onDetectIncome,
  detectionBusy,
}) => {
  // Kept from the last decision while the dialog animates closed, so the copy does not blank out.
  const copy = COPY[decision ?? 'approve']

  if (approved) {
    return (
      <Dialog
        open={decision !== null}
        onClose={onCancel}
        maxWidth="xs"
        fullWidth
        aria-labelledby="review-manual-pay-cycle-title"
        sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
      >
        <DialogTitle id="review-manual-pay-cycle-title" sx={{ fontSize: '18px', fontWeight: 600 }}>
          Manual pay cycle approved
        </DialogTitle>

        <DialogContent>
          <Alert severity="success" sx={{ mb: 1.5 }}>
            The manual pay cycle has been approved.
          </Alert>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Next step</Typography>
          <Typography sx={{ fontSize: '14px', mt: 0.5 }}>
            Detect income so FHS picks up the approved pay cycle. Until then, the primary income
            still reflects the previous detection.
          </Typography>
          {detectionBusy && (
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 1 }}>
              Income detection is already running for this customer. Try again once it finishes.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCancel}>Not now</Button>
          <Button
            variant="contained"
            onClick={onDetectIncome}
            disabled={detectionBusy}
            sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
          >
            Open Detect Income
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={decision !== null}
      // Closing mid-save would hide the outcome, so the dialog stays until the request settles.
      onClose={isSaving ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="review-manual-pay-cycle-title"
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle id="review-manual-pay-cycle-title" sx={{ fontSize: '18px', fontWeight: 600 }}>
        {copy.title}
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: '14px' }}>
          {copy.action} the{' '}
          <strong>{formatEnum(manualIncome.recurringFrequency).toLowerCase()}</strong> pay cycle of{' '}
          <strong>{formatDollars(manualIncome.lastPaidAmount / 100)}</strong>, last paid on{' '}
          <strong>{formatDay(manualIncome.lastPayDay)}</strong>?
        </Typography>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 1 }}>
          {copy.consequence}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            The review failed: {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            textTransform: 'capitalize',
            borderRadius: '8px',
            boxShadow: 'none',
            // Matches the delete-customer confirmation, the other destructive confirm in the panel.
            ...(copy.destructive && {
              backgroundColor: '#C83532',
              '&:hover': { backgroundColor: '#C83532', boxShadow: 'none' },
            }),
          }}
          onClick={onConfirm}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {error ? 'Try again' : copy.action}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
